import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDashboard } from '@/api/dashboard'
import type {
  Dashboard,
  DashboardMachine,
  DashboardPool,
  OverdueChannelRow,
  ReadyToAssembleRow,
  ThisMonth,
  UnpaidSettlements,
} from '@/api/dashboard'
import type { OnboardingTrack } from '@/api/onboarding'
import { poolLabel } from '@/api/pools'
import { useWorkspace } from '@/api/workspace'
import { formatMinutes, versionLabel } from '@/components/batches/batch-format'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney, formatQty } from '@/components/materials/pool-format'
import { SetupChecklist } from '@/components/onboarding/setup-checklist'
import { useCompleteOnboardingWhenDone } from '@/components/onboarding/setup-steps'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { limitingPartLabel } from '@/components/stock/limiting-part'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { isSetupChecklistHidden } from '@/lib/setup-checklist'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/w/$slug/')({
  component: WorkspaceHome,
  staticData: { breadcrumb: 'dashboard.title' },
})

// Home dashboard (UI-SCREENS A-06, prd/00-app-shell.md): an action list, not charts. Each module
// fills in its section as it ships; a section with nothing to act on is hidden, and the
// placeholder shows while no section has anything. Machines, Materials and Ready to assemble (#137)
// are for Owner and Staff alike.
//
// The three revenue sections (#187) — the "this month" card, unpaid settlements per channel and
// channels overdue for a Sales Report — are figures the Owner alone sees: the server leaves the
// keys out for Staff, so there is nothing to hide here; an absent key simply renders nothing. The
// card is completed by 07 (#197) with expenses and net cash.
//
// While onboarding is open the dashboard carries `setup` and the Setup checklist (#230) sits on
// top, in place of the empty placeholder. The Owner may hide it in this browser; then the placeholder
// shows again when nothing else does.
function WorkspaceHome() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const dashboard = useDashboard(slug)
  // Already loaded by the workspace layout.
  const workspace = useWorkspace(slug)
  const isOwner = workspace.data?.role === 'owner'
  const track = (workspace.data?.onboarding.onboarding_track ?? null) as OnboardingTrack | null
  const setup = dashboard.data?.setup
  const [checklistHidden, setChecklistHidden] = useState(() => isSetupChecklistHidden(slug))
  const showChecklist = setup !== undefined && !(isOwner && checklistHidden)
  useCompleteOnboardingWhenDone(slug, setup, track, isOwner)

  const data = dashboard.data
  const hasMain = data ? data.machines.length > 0 || data.ready_to_assemble.length > 0 : false
  const hasAside = data
    ? hasThisMonth(data) ||
      data.materials.total > 0 ||
      (data.unpaid_settlements?.total ?? 0) > 0 ||
      (data.overdue_channels?.length ?? 0) > 0
    : false

  return (
    <section className={cn('flex flex-col gap-4', hasMain && hasAside ? 'max-w-6xl' : 'max-w-3xl')}>
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h1 className="font-heading text-xl font-semibold">{t('dashboard.title')}</h1>
        <p className="text-sm text-muted-foreground">{todayLabel()}</p>
      </header>
      {dashboard.isPending ? (
        <LoadingSkeleton />
      ) : dashboard.isError || !data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : hasSections(data) || showChecklist ? (
        <>
          {showChecklist && setup ? (
            <div className="max-w-3xl">
              <SetupChecklist
                slug={slug}
                setup={setup}
                cogsMissing={data.cogs_settings_missing}
                track={track}
                isOwner={isOwner}
                onHide={() => setChecklistHidden(true)}
              />
            </div>
          ) : null}
          {/* Work to act on (machines, assembly) on the left; figures and follow-ups on the right.
              With only one side present the page stays one narrower column. */}
          <div
            className={cn('grid items-start gap-4', hasMain && hasAside && 'lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]')}
          >
            {hasMain ? (
              <div className="flex min-w-0 flex-col gap-4">
                {data.machines.length > 0 ? <MachinesSection slug={slug} machines={data.machines} /> : null}
                {data.ready_to_assemble.length > 0 ? (
                  <ReadyToAssembleSection slug={slug} rows={data.ready_to_assemble} />
                ) : null}
              </div>
            ) : null}
            {hasAside ? (
              <div className="flex min-w-0 flex-col gap-4">
                {hasThisMonth(data) && data.this_month ? (
                  <ThisMonthCard slug={slug} thisMonth={data.this_month} />
                ) : null}
                {data.materials.total > 0 ? <MaterialsSection slug={slug} materials={data.materials} /> : null}
                {data.unpaid_settlements && data.unpaid_settlements.total > 0 ? (
                  <UnpaidSettlementsSection slug={slug} unpaid={data.unpaid_settlements} />
                ) : null}
                {data.overdue_channels && data.overdue_channels.length > 0 ? (
                  <OverdueChannelsSection slug={slug} rows={data.overdue_channels} />
                ) : null}
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('dashboard.emptyTitle')}</CardTitle>
            <CardDescription>{t('dashboard.emptyBody')}</CardDescription>
          </CardHeader>
          <CardContent />
        </Card>
      )}
    </section>
  )
}

const todayFormat = new Intl.DateTimeFormat('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

function todayLabel(): string {
  return todayFormat.format(new Date())
}

function hasSections(data: Dashboard): boolean {
  return (
    data.machines.length > 0 ||
    data.materials.total > 0 ||
    data.ready_to_assemble.length > 0 ||
    hasThisMonth(data) ||
    (data.unpaid_settlements?.total ?? 0) > 0 ||
    (data.overdue_channels?.length ?? 0) > 0
  )
}

// Nothing in and nothing out this month is nothing to act on, so the card stays hidden; one of
// the two is enough to show all three figures.
function hasThisMonth(data: Dashboard): boolean {
  return (data.this_month?.payments_received ?? 0) > 0 || (data.this_month?.expenses ?? 0) > 0
}

// This month (#187, completed by 07 in #197; Owner only): payments received, expenses and net
// cash, which may be negative. The figures are the current month's line of the monthly summary,
// which the card links to — E-04 carries the detail (owner decision 2026-09-23, Q2).
function ThisMonthCard({ slug, thisMonth }: { slug: string; thisMonth: ThisMonth }) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('dashboard.thisMonth.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <dl className="flex flex-col gap-2">
          <MoneyFigure label={t('dashboard.thisMonth.paymentsReceived')} amount={thisMonth.payments_received} />
          <MoneyFigure label={t('dashboard.thisMonth.expenses')} amount={thisMonth.expenses} />
          <div className="border-t pt-2">
            <MoneyFigure label={t('dashboard.thisMonth.netCash')} amount={thisMonth.net_cash} emphasis />
          </div>
        </dl>
        <Link
          to="/w/$slug/reports/monthly"
          params={{ slug }}
          className="self-start text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          {t('dashboard.thisMonth.seeSummary')}
        </Link>
      </CardContent>
    </Card>
  )
}

// A negative net cash keeps its minus sign in front of the Rp, the way every money figure reads.
function MoneyFigure({ label, amount, emphasis = false }: { label: string; amount: number; emphasis?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'tabular-nums',
          emphasis ? 'text-lg font-semibold' : 'font-medium',
          emphasis && amount < 0 && 'text-destructive',
        )}
      >
        {amount < 0 ? '−' : ''}Rp {formatMoney(Math.abs(amount))}
      </dd>
    </div>
  )
}

// A list row that is one link: the whole row is the target, highlighted on hover and focus.
const rowLinkClass =
  '-mx-2 flex min-h-11 rounded-md px-2 py-2 outline-none hover:bg-muted/60 focus-visible:ring-3 focus-visible:ring-ring/50'

// Unpaid settlements per channel (#187, Owner only): what every Sales Channel still owes over all
// its non-voided Sales Reports, largest first, at most 10; "see all" opens S-09 when more owe.
// Each row opens the channel.
function UnpaidSettlementsSection({ slug, unpaid }: { slug: string; unpaid: UnpaidSettlements }) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('dashboard.unpaidSettlements.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <ul className="flex flex-col">
          {unpaid.channels.map((row) => (
            <li key={row.sales_channel.id}>
              <Link
                to="/w/$slug/channels/$channelId"
                params={{ slug, channelId: String(row.sales_channel.id) }}
                className={cn(rowLinkClass, 'flex-wrap items-center gap-x-3 gap-y-1')}
              >
                <span className="font-medium">{row.sales_channel.name}</span>
                {row.sales_channel.archived ? <Badge variant="outline">{t('salesChannels.archived')}</Badge> : null}
                <span className="ml-auto tabular-nums">Rp {formatMoney(row.outstanding_amount)}</span>
              </Link>
            </li>
          ))}
        </ul>
        {unpaid.total > unpaid.channels.length ? (
          <Button asChild variant="outline" className="h-11 self-start">
            <Link to="/w/$slug/sales-reports/outstanding" params={{ slug }}>
              {t('dashboard.unpaidSettlements.seeAll', { count: unpaid.total })}
            </Link>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}

// Channels overdue for a Sales Report (#187, Owner only): the non-archived channels with
// Settlement cycle days whose last period end is further back than the cycle — or, with no report
// yet, the later of the Adoption date and the channel's creation date (Q1). Longest overdue first;
// each row opens S-06 with the channel prefilled.
function OverdueChannelsSection({ slug, rows }: { slug: string; rows: OverdueChannelRow[] }) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('dashboard.overdueChannels.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col">
          {rows.map((row) => (
            <li key={row.sales_channel.id}>
              <Link
                to="/w/$slug/sales-reports/new"
                params={{ slug }}
                search={{ channelId: row.sales_channel.id }}
                className={cn(rowLinkClass, 'flex-col gap-1')}
              >
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-medium">{row.sales_channel.name}</span>
                  <span className="ml-auto text-sm font-medium text-destructive">
                    {t('dashboard.overdueChannels.daysOverdue', { count: row.days_overdue })}
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {row.last_period_end
                    ? t('dashboard.overdueChannels.lastPeriodEnd', { date: formatBusinessDate(row.last_period_end) })
                    : t('dashboard.overdueChannels.neverReported', { date: formatBusinessDate(row.since) })}
                  {' · '}
                  {t('dashboard.overdueChannels.cycle', { count: row.sales_channel.settlement_cycle_days })}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

// Every non-archived Machine (#137), one tile each: printing, with its Batch and the estimated
// progress (elapsed time against the Batch's snapshotted est. minutes, #126 Q4, labelled
// "estimate") and *Complete*; or idle, with *Start batch*.
function MachinesSection({ slug, machines }: { slug: string; machines: DashboardMachine[] }) {
  const { t } = useTranslation()
  const printing = machines.filter((machine) => machine.printing_batch).length

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('dashboard.machines.title')}</CardTitle>
        <CardAction className="text-sm text-muted-foreground">
          {t('dashboard.machines.summary', { printing, total: machines.length })}
        </CardAction>
      </CardHeader>
      <CardContent>
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {machines.map((machine) => (
            <li key={machine.id} className="flex">
              <MachineTile slug={slug} machine={machine} />
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function MachineTile({ slug, machine }: { slug: string; machine: DashboardMachine }) {
  const { t } = useTranslation()
  const batch = machine.printing_batch

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-3 rounded-lg border p-3',
        batch ? 'border-foreground/20 bg-muted/40' : 'border-dashed',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <Link
          to="/w/$slug/machines/$machineId"
          params={{ slug, machineId: String(machine.id) }}
          className="truncate font-medium underline-offset-4 hover:underline"
        >
          {machine.name}
        </Link>
        <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
          <span
            aria-hidden="true"
            className={cn(
              'size-2 rounded-full',
              batch ? 'bg-emerald-500 motion-safe:animate-pulse' : 'bg-muted-foreground/40',
            )}
          />
          {batch ? t('dashboard.machines.printingStatus') : t('dashboard.machines.idle')}
        </span>
      </div>
      {batch ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-sm">
            <Link
              to="/w/$slug/batches/$batchId"
              params={{ slug, batchId: String(batch.id) }}
              className="font-medium underline underline-offset-4"
            >
              {batch.batch_no}
            </Link>
            <span className="text-muted-foreground"> · {versionLabel(t, batch)}</span>
          </p>
          <div className="flex items-center gap-2">
            <div
              role="progressbar"
              aria-label={t('dashboard.machines.progressLabel', { batchNo: batch.batch_no })}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={batch.progress_percent}
              className="h-2 flex-1 overflow-hidden rounded-full bg-background ring-1 ring-foreground/10"
            >
              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${batch.progress_percent}%` }} />
            </div>
            <span className="text-sm font-medium tabular-nums">{batch.progress_percent}%</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('dashboard.machines.estimate')}:{' '}
            {t('dashboard.machines.progress', {
              elapsed: formatMinutes(t, batch.elapsed_minutes),
              total: formatMinutes(t, batch.est_minutes),
            })}
          </p>
        </div>
      ) : null}
      {batch ? (
        batch.can.complete ? (
          <Button asChild className="mt-auto h-11 w-full">
            <Link to="/w/$slug/batches/$batchId/complete" params={{ slug, batchId: String(batch.id) }}>
              {t('batches.detail.complete')}
            </Link>
          </Button>
        ) : null
      ) : machine.can.start_batch ? (
        <Button asChild variant="outline" className="mt-auto h-11 w-full">
          <Link to="/w/$slug/batches/new" params={{ slug }} search={{ machineId: machine.id }}>
            {t('batches.start')}
          </Link>
        </Button>
      ) : null}
    </div>
  )
}

// Buildable today per non-archived Variant of a non-archived Product (#137, 04g): Buildable > 0
// first, "+N with substitutes" when substitute WIP would add some, the reason when there is no BOM
// in effect. Each row opens F-10 with the Variant picked; the count sits on the right so the list
// scans as a column of numbers.
function ReadyToAssembleSection({ slug, rows }: { slug: string; rows: ReadyToAssembleRow[] }) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('dashboard.readyToAssemble.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col divide-y">
          {rows.map((row) => (
            <li key={row.variant.id} className="py-1 first:pt-0 last:pb-0">
              <ReadyToAssembleRowView slug={slug} row={row} />
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function ReadyToAssembleRowView({ slug, row }: { slug: string; row: ReadyToAssembleRow }) {
  const { t } = useTranslation()
  const extra = row.buildable !== null && row.with_substitutes !== null ? row.with_substitutes - row.buildable : 0

  return (
    <Link
      to="/w/$slug/assemblies/new"
      params={{ slug }}
      search={{ variantId: row.variant.id }}
      className={cn(rowLinkClass, 'items-center gap-4')}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">
            {row.variant.sku} · {row.variant.name}
          </span>
          {row.needs_color ? <Badge variant="outline">{t('dashboard.readyToAssemble.needsColor')}</Badge> : null}
        </span>
        <span className="text-xs text-muted-foreground">
          {row.variant.product.code} · {row.variant.product.name}
        </span>
        {row.buildable === null ? (
          <span className="text-xs text-muted-foreground">
            {t(`assemblies.buildableReason.${row.reason ?? 'no_bom'}`)}
          </span>
        ) : row.limiting ? (
          <span className="text-xs text-muted-foreground">{limitingPartLabel(t, row.limiting)}</span>
        ) : null}
      </span>
      {row.buildable === null ? (
        <span className="text-2xl font-semibold text-muted-foreground/50" aria-hidden="true">
          —
        </span>
      ) : (
        <span className="flex shrink-0 flex-col items-end">
          <span className="sr-only">{t('dashboard.readyToAssemble.buildable', { count: row.buildable })}</span>
          <span
            aria-hidden="true"
            className={cn(
              'text-2xl font-semibold tabular-nums',
              row.buildable > 0 ? 'text-foreground' : 'text-muted-foreground/50',
            )}
          >
            {formatQty(row.buildable)}
          </span>
          {extra > 0 ? (
            <span className="text-xs text-muted-foreground">
              {t('assemblies.form.withSubstitutes', { count: extra })}
            </span>
          ) : null}
        </span>
      )}
    </Link>
  )
}

// Pools below their minimum or negative ("needs stock count"), worst first, at most 10 (#69);
// when more need attention, "see all" opens M-01 filtered to all of them. Each row shows how far
// the Pool is from its minimum as a meter.
function MaterialsSection({ slug, materials }: { slug: string; materials: Dashboard['materials'] }) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('dashboard.materials.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <ul className="flex flex-col">
          {materials.pools.map((pool) => (
            <li key={pool.id}>
              <PoolRow slug={slug} pool={pool} />
            </li>
          ))}
        </ul>
        {materials.total > materials.pools.length ? (
          <Button asChild variant="outline" className="h-11 self-start">
            <Link to="/w/$slug/materials" params={{ slug }} search={{ attention: true }}>
              {t('dashboard.materials.seeAll', { count: materials.total })}
            </Link>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}

function PoolRow({ slug, pool }: { slug: string; pool: DashboardPool }) {
  const { t } = useTranslation()
  const qty = `${formatQty(pool.qty)} ${pool.unit}`
  const percent = pool.min_qty ? Math.max(0, Math.min(100, Math.round((pool.qty / pool.min_qty) * 100))) : null

  return (
    <Link
      to="/w/$slug/materials/pools/$poolId"
      params={{ slug, poolId: String(pool.id) }}
      className={cn(rowLinkClass, 'flex-col gap-1.5 py-2.5')}
    >
      {/* Every Pool here is below its minimum, so only the stronger "needs stock count" flag shows. */}
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{poolLabel(pool)}</span>
        {pool.needs_stock_count ? <Badge variant="destructive">{t('materials.flags.needsStockCount')}</Badge> : null}
      </span>
      {percent !== null && pool.min_qty !== null ? (
        <span className="flex flex-col gap-1">
          <span
            aria-hidden="true"
            className="h-1.5 w-full overflow-hidden rounded-full bg-muted ring-1 ring-foreground/5"
          >
            <span className="block h-full rounded-full bg-amber-500" style={{ width: `${percent}%` }} />
          </span>
          <span className={cn('text-xs tabular-nums', pool.qty < 0 ? 'text-destructive' : 'text-muted-foreground')}>
            {t('dashboard.materials.ofMinimum', { qty, min: `${formatQty(pool.min_qty)} ${pool.unit}` })}
          </span>
        </span>
      ) : (
        <span className={cn('text-xs tabular-nums', pool.qty < 0 ? 'text-destructive' : 'text-muted-foreground')}>
          {qty} · {t('materials.noMinimum')}
        </span>
      )}
    </Link>
  )
}
